import { Canvas, type CanvasProps } from '@react-three/fiber';
import { View } from '@react-three/drei';
import type { CSSProperties, ReactNode } from 'react';
import { useViewportHost } from './ViewportHost';

export interface ViewportProps {
  className?: string;
  style?: CSSProperties;
  /**
   * Fallback-only: forwarded to the standalone `<Canvas>` this viewport
   * owns when no `<ViewportHost>` ancestor is present (an external
   * `@sstk/build-kit` consumer with no Studio shell around it), so that
   * behaviour matches what this mount site declared before joining the
   * shared canvas. Ignored under a host, whose renderer settings are fixed
   * once at the host's own level (see ViewportHost.tsx) - a shared WebGL
   * context can't carry different shadow-map/antialias settings per view.
   */
  shadows?: CanvasProps['shadows'];
  gl?: CanvasProps['gl'];
  children: ReactNode;
}

const FILL_STYLE: CSSProperties = { position: 'relative', width: '100%', height: '100%', overflow: 'hidden' };

/**
 * One logical viewport. Renders via `@react-three/drei`'s `<View>` into the
 * nearest ancestor `<ViewportHost>`'s shared canvas when one is present;
 * otherwise falls back to owning a plain `<Canvas>` (matches every mount
 * site's original, pre-consolidation behaviour) - several of the
 * components using this (GameRuntime, CharacterStudio, the gallery stages)
 * are also published, standalone `@sstk/build-kit` exports a game host can
 * mount with no Studio shell around them at all.
 *
 * Deliberately does NOT pass its own `track` ref to `<View>`: outside a
 * Canvas context drei's `<View>` renders through a DOM-side tunnel
 * (`HtmlView`) that creates and measures its OWN internal tracking
 * element regardless of any `track` prop passed in - `className`/`style`
 * are what actually land on that element, so those are forwarded directly
 * and `<View>` is left to own the DOM node. Verified by spike
 * (spike-entry.tsx, since deleted): a `track` ref pointed at a separately
 * rendered, correctly-sized wrapper div was silently ignored, leaving
 * `<View>`'s own real tracking element unstyled and hence zero-height.
 *
 * A per-viewport pointer-miss handler can't be passed as a prop here for
 * the same reason - extra props on `<View>` land on that plain tracking
 * `<div>` in this mode, never on the portalled scene root, so it would
 * never fire on a real R3F event. Wrap the affected content in a
 * `<group onPointerMissed={...}>` inside `children` instead (this repo's
 * `GameRuntime` does this for its edit-mode placement-miss hint); a plain
 * R3F event handler on any scene object works identically whether that
 * object ends up inside a directly-owned `<Canvas>` or projected through
 * `<View>`.
 */
export function Viewport({ className, style, shadows, gl, children }: ViewportProps) {
  const hosted = useViewportHost();

  if (hosted) {
    return (
      <View className={className} style={style ? { ...FILL_STYLE, ...style } : FILL_STYLE}>
        {children}
      </View>
    );
  }

  return (
    <Canvas className={className} style={style} shadows={shadows} gl={gl}>
      {children}
    </Canvas>
  );
}
